import { useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Loader2, AlertCircle } from 'lucide-react';
import { pipelineService } from '@services/pipeline.service';
import type { Stage } from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

export interface ReopenPayload {
  reason: string;
  stageId?: string;
}

interface ReopenTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ReopenPayload) => Promise<void>;
  /** Ticket number shown in the header */
  ticketNumber?: string;
  /** Ticket subject, for context */
  ticketSubject?: string;
  /** Pipeline whose stages should populate the dropdown */
  pipelineId?: string | null;
  /** Pre-selected stage id. Defaults to the stage with mappedStatus === 'in_progress'. */
  defaultStageId?: string;
  isSubmitting?: boolean;
}

const MIN_REASON = 3;
const MAX_REASON = 1000;

export function ReopenTicketModal({
  isOpen,
  onClose,
  onSubmit,
  ticketNumber,
  ticketSubject,
  pipelineId,
  defaultStageId,
  isSubmitting,
}: ReopenTicketModalProps) {
  const [reason, setReason] = useState('');
  const [stageId, setStageId] = useState<string>('');
  const [stages, setStages] = useState<Stage[]>([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [stagesError, setStagesError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const reasonInputRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setTouched(false);
      setStageId('');
      // Auto-focus the reason textarea after the next paint so the user can
      // start typing immediately.
      const raf = requestAnimationFrame(() => reasonInputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen]);

  // Escape-to-close + Tab focus trap
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  // Load stages when pipeline id is known
  useEffect(() => {
    if (!isOpen || !pipelineId) {
      setStages([]);
      return;
    }
    let cancelled = false;
    setStagesLoading(true);
    setStagesError(null);
    pipelineService
      .getStages(pipelineId)
      .then((fetched) => {
        if (cancelled) return;
        const sorted = [...fetched].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setStages(sorted);
        // Pick default: explicit, then in_progress, then first stage.
        const defaultStage =
          sorted.find((s) => s.id === defaultStageId) ||
          sorted.find((s) => (s as any).mappedStatus === 'in_progress') ||
          sorted[0];
        if (defaultStage) setStageId(defaultStage.id);
      })
      .catch((err) => {
        if (cancelled) return;
        setStagesError(err?.message || 'Failed to load stages');
      })
      .finally(() => {
        if (!cancelled) setStagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, pipelineId, defaultStageId]);

  const reasonError = useMemo(() => {
    const trimmed = reason.trim();
    if (!trimmed) return 'Reason is required';
    if (trimmed.length < MIN_REASON) return `Reason must be at least ${MIN_REASON} characters`;
    if (trimmed.length > MAX_REASON) return `Reason must be at most ${MAX_REASON} characters`;
    return null;
  }, [reason]);

  const canSubmit = !reasonError && !isSubmitting && !stagesLoading;

  const handleSubmit = async () => {
    setTouched(true);
    if (reasonError) return;
    await onSubmit({
      reason: reason.trim(),
      stageId: stageId || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reopen-modal-title"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-9 w-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0">
              <RotateCcw className="h-4 w-4" />
            </div>
            <div>
              <h2 id="reopen-modal-title" className="text-base font-semibold text-gray-900 dark:text-white">
                Reopen ticket{ticketNumber ? ` #${ticketNumber}` : ''}
              </h2>
              {ticketSubject && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                  {ticketSubject}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50 dark:text-gray-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Reason */}
          <div>
            <label htmlFor="reopen-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Reason for reopening <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reopen-reason"
              ref={reasonInputRef}
              rows={4}
              maxLength={MAX_REASON}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              disabled={isSubmitting}
              placeholder="e.g. Customer reported the issue is still happening after closure"
              className={`w-full rounded-lg border bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 px-3 py-2 focus:outline-none focus:ring-2 transition disabled:opacity-60 ${
                touched && reasonError
                  ? 'border-red-300 dark:border-red-600 focus:ring-red-200 dark:focus:ring-red-800'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-primary-200 dark:focus:ring-primary-800 focus:border-primary-500'
              }`}
            />
            <div className="flex items-center justify-between mt-1">
              {touched && reasonError ? (
                <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {reasonError}
                </p>
              ) : (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Shown in the ticket timeline and the assignee's notification.
                </span>
              )}
              <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                {reason.trim().length}/{MAX_REASON}
              </span>
            </div>
          </div>

          {/* Stage */}
          <div>
            <label htmlFor="reopen-stage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Reopen to stage
            </label>
            {stagesError ? (
              <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {stagesError}
              </p>
            ) : stagesLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading stages…
              </div>
            ) : stages.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No pipeline is associated with this ticket. It will be reopened without a stage change.
              </p>
            ) : (
              <ThemedSelect
                id="reopen-stage"
                value={stageId}
                onChange={(e) => setStageId(e.target.value)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800 focus:border-primary-500 disabled:opacity-60"
              >
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {(s as any).mappedStatus === 'in_progress' ? '  (default)' : ''}
                  </option>
                ))}
              </ThemedSelect>
            )}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Defaults to the In Progress lane. You can pick any stage in the pipeline.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Reopening…
              </>
            ) : (
              <>
                <RotateCcw className="h-4 w-4" />
                Reopen ticket
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
